import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import VideoSection from '../components/home/VideoSection';
import FacilitiesSection from '../components/home/FacilitiesSection';
import ourStoryImage from '../assets/1.webp';
import ScrollToBottomButton from '../components/ui/ScrollToBottomButton';

const AboutUs = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <>
      <section
        className="relative h-[60vh] bg-cover bg-center flex items-center justify-center text-white"
        style={{ backgroundImage: `url(${ourStoryImage})` }}
        data-aos="fade-in"
      >
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-aos="fade-up">About Pineridge Hotel</h1>
          <p className="mt-4 text-lg text-gray-300" data-aos="fade-up" data-aos-delay="100">Discover our story and commitment to hospitality</p>
        </div>
        <ScrollToBottomButton targetId="about-content" />
      </section>

      <main id="about-content" className="bg-gray-50 py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-5xl font-semibold text-dark-green">Welcome to Pineridge Hotel</h2>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              A charming retreat in the heart of Darjeeling, where comfort meets the majestic beauty of the Himalayas.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg mb-12" data-aos="fade-up">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-semibold text-dark-green mb-4">Our Story</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
The majestic beauty of the Himalayas has for long been legendary. Nestled within this grandeur and in the shadow of Mt. Kanchenjunga is the town of Darjeeling Land of the Thunderbolt, a mosaic of exotic bazaars and alleys, charming villas, gardens and woods, a vibrant populace and a long and colorful history.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
Forming a part of this heritage and located at its very heart at the Town Square, or 'Chowrasta', is Pineridge Hotel with its instantly recognizable and intricate facade and architectural grandeur, set within sprawling lawns and gardens dotted with a representative selection of the multifarious flora of the region, truly a landmark of the town in its own right.
                </p>
                <p className="text-gray-700 leading-relaxed">
Continuing in the spirit and traditions of the Drum Druid Hotel, the foremost hotel of days long past which once stood at the very same spot, each of the hotel's forty spacious rooms has its own individual character and ambience and a range of views of the mountains, gardens, the Mall and Darjeeling town. Individual fireplaces, original antique teak and mahogany furniture, bay windows and original paintings by local master artists combine with all modern amenities a unique blend of modern comfort in a traditional setting.
</p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img src={ourStoryImage} alt="Pineridge Hotel" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          <div className="text-center my-16" data-aos="fade-up">
            <h3 className="text-2xl font-semibold text-dark-green">Our Mission</h3>
            <p className="mt-4 max-w-3xl mx-auto text-lg text-gray-600 leading-relaxed">
              To be a home for travelers from around the world seeking authenticity, comfort, and a sense of belonging. We are committed to providing an unforgettable experience that captures the essence of the Himalayas.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg" data-aos="fade-up">
            <h3 className="text-2xl font-semibold text-center text-dark-green mb-6">Why Choose Us?</h3>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="mb-4 text-beige">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <h4 className="text-lg font-semibold text-dark-green">Prime Location</h4>
                <p className="text-gray-600">Steps away from Darjeeling's main attractions, yet offering serene tranquility.</p>
              </div>
              <div>
                <div className="mb-4 text-beige">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <h4 className="text-lg font-semibold text-dark-green">Open Spaces</h4>
                <p className="text-gray-600">Pineridge Hotel offers serene garden spaces where guests can relax and enjoy fresh mountain air amidst classic greenery in the heart of Darjeeling.</p>
              </div>
              <div>
                <div className="mb-4 text-beige">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                </div>
                <h4 className="text-lg font-semibold text-dark-green">Heritage Charm</h4>
                <p className="text-gray-600">Pineridge Hotel showcases true heritage charm with classic colonial architecture and timeless details, offering an authentic old-world Darjeeling experience.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <VideoSection />
      <FacilitiesSection />
    </>
  );
};

export default AboutUs;